import { apiClient } from "../api/apiClient.js";

// Thin fetch wrappers over the Prompt 131 Backoffice Worker Line Admin API
// (BackofficeWorkerLineController/WorkerLineAdminService): SuperAdmin-only
// create/update of Super Agent / PLC / Agent organization rows, scope-
// isolated list/detail reads for everyone else. Same base/auth convention as
// ../geofence/geofenceApi.js - apiClient.get/post/patch with no `base`
// override. This file never calls a hierarchy-tree/invite-card/worker-
// application API (those belong to ../hierarchy/useHierarchyTree.js and
// other, separate packets).

const WORKER_LINE_PATH = "/backoffice/worker-line";

// Exactly the query param names BackofficeWorkerLineController.ListSuperAgents
// declares, in the same order - anything else passed to listSuperAgents is
// silently dropped rather than forwarded.
const SUPER_AGENTS_LIST_PARAM_KEYS = ["search", "status", "page", "pageSize"];

// BackofficeWorkerLineController.ListPlcs's own param order. P161 appended
// organizationSide - "JobDemand" for บริษัทจัดหางาน, "WorkerSupply" for
// บริษัทจัดหาแรงงาน, omitted entirely for the unfiltered both-sides list.
const PLCS_LIST_PARAM_KEYS = ["superAgentOrganizationId", "search", "status", "page", "pageSize", "organizationSide"];

// BackofficeWorkerLineController.ListAgents's own param order.
const AGENTS_LIST_PARAM_KEYS = ["superAgentOrganizationId", "plcOrganizationId", "search", "status", "page", "pageSize"];

/** Builds a query string with every empty/null/undefined value omitted entirely (never sent as `key=`), restricted to `paramKeys`. */
function buildQuery(paramKeys, params = {}) {
  const searchParams = new URLSearchParams();
  for (const key of paramKeys) {
    const value = params[key];
    if (value === null || value === undefined || value === "") {
      continue;
    }
    searchParams.set(key, String(value));
  }
  const query = searchParams.toString();
  return query ? `?${query}` : "";
}

// ============================================================
// Super Agents
// ============================================================

export function listSuperAgents(params = {}) {
  return apiClient.get(`${WORKER_LINE_PATH}/super-agents${buildQuery(SUPER_AGENTS_LIST_PARAM_KEYS, params)}`);
}

export function getSuperAgent(superAgentOrganizationId) {
  if (!superAgentOrganizationId) {
    throw new Error("getSuperAgent requires a superAgentOrganizationId");
  }
  return apiClient.get(`${WORKER_LINE_PATH}/super-agents/${encodeURIComponent(superAgentOrganizationId)}`);
}

// Matches CreateSuperAgentRequest's own field set exactly
// (Back/WorkmateX/API/RootAPI/Models/Backoffice/WorkerLineAdminDtos.cs).
export function createSuperAgent({ displayName, legalName, ownerName, primaryPhoneNumber, lineId, note } = {}) {
  return apiClient.post(`${WORKER_LINE_PATH}/super-agents`, {
    displayName: displayName || null,
    legalName: legalName || null,
    ownerName: ownerName || null,
    primaryPhoneNumber: primaryPhoneNumber || null,
    lineId: lineId || null,
    note: note || null,
  });
}

// Every field is optional/omitted-when-undefined - matches
// UpdateSuperAgentRequest's own "left out of the request body leaves the
// column untouched" contract. JSON.stringify drops `undefined` keys, which
// is what actually implements "omit".
export function updateSuperAgent(superAgentOrganizationId, { displayName, legalName, ownerName, primaryPhoneNumber, lineId, status, note } = {}) {
  if (!superAgentOrganizationId) {
    throw new Error("updateSuperAgent requires a superAgentOrganizationId");
  }
  const body = {};
  if (displayName) body.displayName = displayName;
  if (legalName !== undefined) body.legalName = legalName || null;
  if (ownerName !== undefined) body.ownerName = ownerName || null;
  if (primaryPhoneNumber !== undefined) body.primaryPhoneNumber = primaryPhoneNumber || null;
  if (lineId !== undefined) body.lineId = lineId || null;
  if (status) body.status = status;
  if (note !== undefined) body.note = note || null;
  return apiClient.patch(`${WORKER_LINE_PATH}/super-agents/${encodeURIComponent(superAgentOrganizationId)}`, body);
}

// ============================================================
// PLC / บมจ
// ============================================================

export function listPlcs(params = {}) {
  return apiClient.get(`${WORKER_LINE_PATH}/plcs${buildQuery(PLCS_LIST_PARAM_KEYS, params)}`);
}

export function getPlc(plcOrganizationId) {
  if (!plcOrganizationId) {
    throw new Error("getPlc requires a plcOrganizationId");
  }
  return apiClient.get(`${WORKER_LINE_PATH}/plcs/${encodeURIComponent(plcOrganizationId)}`);
}

// superAgentOrganizationId is required by CreatePlcRequest's own contract -
// a PLC never exists without a real parent Super Agent, so this file never
// sends a create request with a blank/invented parent id.
export function createPlc({ superAgentOrganizationId, displayName, legalName, ownerName, primaryPhoneNumber, lineId, note } = {}) {
  if (!superAgentOrganizationId) {
    throw new Error("createPlc requires a superAgentOrganizationId");
  }
  return apiClient.post(`${WORKER_LINE_PATH}/plcs`, {
    superAgentOrganizationId,
    displayName: displayName || null,
    legalName: legalName || null,
    ownerName: ownerName || null,
    primaryPhoneNumber: primaryPhoneNumber || null,
    lineId: lineId || null,
    note: note || null,
  });
}

export function updatePlc(plcOrganizationId, { displayName, legalName, ownerName, primaryPhoneNumber, lineId, status, note } = {}) {
  if (!plcOrganizationId) {
    throw new Error("updatePlc requires a plcOrganizationId");
  }
  const body = {};
  if (displayName) body.displayName = displayName;
  if (legalName !== undefined) body.legalName = legalName || null;
  if (ownerName !== undefined) body.ownerName = ownerName || null;
  if (primaryPhoneNumber !== undefined) body.primaryPhoneNumber = primaryPhoneNumber || null;
  if (lineId !== undefined) body.lineId = lineId || null;
  if (status) body.status = status;
  if (note !== undefined) body.note = note || null;
  return apiClient.patch(`${WORKER_LINE_PATH}/plcs/${encodeURIComponent(plcOrganizationId)}`, body);
}

// ============================================================
// Agents
// ============================================================

export function listAgents(params = {}) {
  return apiClient.get(`${WORKER_LINE_PATH}/agents${buildQuery(AGENTS_LIST_PARAM_KEYS, params)}`);
}

export function getAgent(agentId) {
  if (!agentId) {
    throw new Error("getAgent requires an agentId");
  }
  return apiClient.get(`${WORKER_LINE_PATH}/agents/${encodeURIComponent(agentId)}`);
}

// Both superAgentOrganizationId and plcOrganizationId are required by
// CreateWorkerLineAgentRequest's own contract - an Agent never exists
// without a real parent Super Agent and a real parent PLC, so this file
// never sends a create request with a blank/invented parent id.
//
// Prompt 136: createLoginAccount/loginIdentifier/temporaryPassword are only
// added to the request body when createLoginAccount is truthy - a plain
// Agent create (createLoginAccount left false/undefined) sends exactly the
// same body shape this endpoint already accepted before Prompt 136.
export function createAgent({
  superAgentOrganizationId,
  plcOrganizationId,
  displayName,
  legalName,
  ownerName,
  primaryPhoneNumber,
  lineId,
  note,
  createLoginAccount,
  loginIdentifier,
  temporaryPassword,
} = {}) {
  if (!superAgentOrganizationId) {
    throw new Error("createAgent requires a superAgentOrganizationId");
  }
  if (!plcOrganizationId) {
    throw new Error("createAgent requires a plcOrganizationId");
  }
  const body = {
    superAgentOrganizationId,
    plcOrganizationId,
    displayName: displayName || null,
    legalName: legalName || null,
    ownerName: ownerName || null,
    primaryPhoneNumber: primaryPhoneNumber || null,
    lineId: lineId || null,
    note: note || null,
  };
  if (createLoginAccount) {
    body.createLoginAccount = true;
    body.loginIdentifier = loginIdentifier || null;
    body.temporaryPassword = temporaryPassword || null;
  }
  return apiClient.post(`${WORKER_LINE_PATH}/agents`, body);
}

export function updateAgent(agentId, { displayName, legalName, ownerName, primaryPhoneNumber, lineId, status, note } = {}) {
  if (!agentId) {
    throw new Error("updateAgent requires an agentId");
  }
  const body = {};
  if (displayName) body.displayName = displayName;
  if (legalName !== undefined) body.legalName = legalName || null;
  if (ownerName !== undefined) body.ownerName = ownerName || null;
  if (primaryPhoneNumber !== undefined) body.primaryPhoneNumber = primaryPhoneNumber || null;
  if (lineId !== undefined) body.lineId = lineId || null;
  if (status) body.status = status;
  if (note !== undefined) body.note = note || null;
  return apiClient.patch(`${WORKER_LINE_PATH}/agents/${encodeURIComponent(agentId)}`, body);
}

// P161: attaches the Agent's own picture. SuperAdmin-only server-side, exactly
// like every other Agent write here.
//
// Sent as multipart FormData under the field name the route binds ("photo") -
// passed to apiClient.postForm untouched so the browser sets the multipart
// boundary itself. This file never sends the picture as a base64 string in a
// JSON body: the endpoint takes a real file, and hand-encoding one would both
// inflate it by a third and bypass the server's own magic-number check.
export function uploadAgentPhoto(agentId, file) {
  if (!agentId) {
    throw new Error("uploadAgentPhoto requires an agentId");
  }
  if (!file) {
    throw new Error("uploadAgentPhoto requires a file");
  }
  const formData = new FormData();
  formData.append("photo", file);
  return apiClient.postForm(`${WORKER_LINE_PATH}/agents/${encodeURIComponent(agentId)}/photo`, formData);
}

/**
 * P161: the Agent picture's real bytes, as a blob.
 *
 * Read through apiClient.getBlob rather than by pointing an <img src> straight
 * at the URL: this endpoint requires the session bearer token, which a plain
 * <img> request would never carry, so the image would silently 401. The caller
 * turns the blob into an object URL and is responsible for revoking it.
 */
export function getAgentPhotoBlob(agentId) {
  if (!agentId) {
    throw new Error("getAgentPhotoBlob requires an agentId");
  }
  return apiClient.getBlob(`${WORKER_LINE_PATH}/agents/${encodeURIComponent(agentId)}/photo/file`);
}

// Prompt 136: SuperAdmin-only. Creates a Backoffice login account for this
// Agent if it has none yet, or resets the password (and optionally
// loginIdentifier) of an existing one - same route either way, the backend
// decides create-vs-reset from whether a login account already exists.
export function createOrResetAgentLoginAccount(agentId, { loginIdentifier, temporaryPassword } = {}) {
  if (!agentId) {
    throw new Error("createOrResetAgentLoginAccount requires an agentId");
  }
  return apiClient.post(`${WORKER_LINE_PATH}/agents/${encodeURIComponent(agentId)}/login-account`, {
    loginIdentifier: loginIdentifier || null,
    temporaryPassword: temporaryPassword || null,
  });
}
